/**
 * HTTP client for the Worker account + campaign surface.
 *
 * Mirrors the discriminated-union error shape used by `auth/api.ts` so
 * the picker view can render a single inline-error component regardless
 * of which call failed.
 *
 * Why a dedicated module (and not a re-export from app.ts)? The picker
 * view is mounted BEFORE the substrate opens — we have no replica yet,
 * no SyncClient, and no Campaign DO. These are plain credentialed
 * fetches against the Worker registry endpoints.
 */
import { workerOrigin } from '../worker-origin.js';

export type CampaignsResult<T> =
  | { ok: true; value: T }
  | { ok: false; status: number; error: string };

export interface CampaignSummary {
  id: string;
  name: string;
  owner_account_id: string;
  created_at: string;
  facilitation_mode: 'gm' | 'solo' | 'rotating' | 'flat';
}

export interface AccountInfo {
  id: string;
  email: string;
}

export interface AccountWithCampaigns {
  account: AccountInfo;
  campaigns: CampaignSummary[];
}

interface ErrorBody {
  error?: string;
  issues?: { path: (string | number)[]; message: string }[];
}

async function request<T>(path: string, init: RequestInit): Promise<CampaignsResult<T>> {
  try {
    const res = await fetch(`${workerOrigin()}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        ...(init.headers ?? {}),
      },
    });
    if (!res.ok) {
      let parsed: ErrorBody = {};
      try {
        parsed = (await res.json()) as ErrorBody;
      } catch {
        // non-JSON; carry status only.
      }
      return {
        ok: false,
        status: res.status,
        error: parsed.error ?? `Request failed (${res.status}).`,
      };
    }
    const value = (await res.json()) as T;
    return { ok: true, value };
  } catch (err) {
    return {
      ok: false,
      status: 0,
      error: err instanceof Error ? err.message : 'Network error.',
    };
  }
}

/**
 * Fetch the signed-in Account plus the list of Campaigns the user can access.
 * Backed by `GET /v1/account`.
 */
export function fetchAccount(): Promise<CampaignsResult<AccountWithCampaigns>> {
  return request<AccountWithCampaigns>('/v1/account', { method: 'GET' });
}

/** Fetch just the Campaigns list for the signed-in Account. */
export async function listCampaigns(): Promise<CampaignsResult<{ campaigns: CampaignSummary[] }>> {
  const result = await fetchAccount();
  if (!result.ok) return result;
  return { ok: true, value: { campaigns: result.value.campaigns } };
}

export function createCampaign(args: {
  name: string;
  facilitation_mode?: 'gm' | 'solo' | 'rotating' | 'flat';
  campaign_kind?: string;
  setting_summary?: string;
}): Promise<CampaignsResult<CampaignSummary>> {
  return request<CampaignSummary>('/v1/campaigns', {
    method: 'POST',
    body: JSON.stringify(args),
  });
}
